import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Loader2, Mail, Lock, User as UserIcon, Ticket, Fingerprint, Eye, EyeOff, AlertTriangle, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { passkeyLoginStart, passkeyLoginFinish } from "@/lib/api";
import { StandardsBadges } from "@/components/StandardsBadges";
import { scorePassword } from "@/lib/passwordStrength";
import { toast } from "sonner";

// Base64url helpers for WebAuthn signin
const _b64urlDecode = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};
const _b64urlEncode = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
};
const _passkeysSupported =
  typeof window !== "undefined" && !!window.PublicKeyCredential &&
  typeof navigator.credentials?.get === "function";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleSignIn = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleGlyph = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.7 14.7 2.8 12 2.8 6.9 2.8 2.8 6.9 2.8 12S6.9 21.2 12 21.2c6.9 0 9.2-4.8 9.2-7.3 0-.5-.05-.9-.12-1.3H12z"/>
    <path fill="#34A853" d="M3.7 7.4l3.2 2.4c.9-1.7 2.5-2.9 4.5-2.9 1.4 0 2.5.5 3.3 1l2.5-2.5C15.8 4 14 3.3 12 3.3 8.4 3.3 5.3 5 3.7 7.4z"/>
    <path fill="#FBBC05" d="M12 20.7c2 0 3.7-.6 4.9-1.8l-2.4-2c-.7.5-1.6.8-2.5.8-1.9 0-3.6-1.3-4.2-3.1l-3.2 2.4C6 19 8.8 20.7 12 20.7z"/>
    <path fill="#4285F4" d="M20.9 11.6H12v3.4h5.1c-.2.9-.9 1.7-1.7 2.2l2.4 2c1.4-1.3 2.3-3.3 2.3-5.6 0-.7-.1-1.3-.2-2z"/>
  </svg>
);

export default function AuthPage() {
  const { login, register, refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();
  // Read URL params once up-front — supports both /register?code=LECT-... and
  // /register?group=CODE flows without any extra clicks.
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const prefillCode = urlParams?.get("code") || "";
  const prefillRef = urlParams?.get("ref") || "";
  const prefillTeacher = urlParams?.get("teacher") === "1";
  const groupCode = urlParams?.get("group") || null;
  // Open register mode if the URL hints at it (/register, ?code=, ?ref=, ?teacher=1).
  const onRegisterPath = typeof window !== "undefined" && /\/register/i.test(window.location.pathname);
  // "Who are you?" role picker — the ONE-URL system. One getlectura.app link
  // for everyone; app sorts them. URL shortcuts still auto-select the right role.
  const initialRole = prefillCode ? "beta" : prefillRef ? "friend" : prefillTeacher ? "teacher" : "student";
  const [mode, setMode] = useState(prefillCode || prefillRef || prefillTeacher || onRegisterPath ? "register" : "login");
  const [whoAmI, setWhoAmI] = useState(initialRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState(prefillCode.toUpperCase());
  const [friendRef, setFriendRef] = useState(prefillRef);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(true);   // Implicit acceptance via account-creation click. Fine print below Submit is the legal record.
  const [showEmail, setShowEmail] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const referred = typeof window !== "undefined" ? !!localStorage.getItem("lectura_ref") : false;

  const strength = useMemo(
    () => (mode === "register" ? scorePassword(password) : { score: 0, label: "", hint: "" }),
    [password, mode]
  );
  const strengthColors = ["bg-destructive", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-600"];
  const handlePasswordKey = (e) => {
    // Cross-browser Caps Lock detection — works on all modern browsers.
    if (typeof e.getModifierState === "function") {
      setCapsOn(e.getModifierState("CapsLock"));
    }
  };

  const signInWithPasskey = async () => {
    if (!email.trim()) { setError("Enter your email first, then tap Passkey."); return; }
    if (!_passkeysSupported) { toast.error("Your browser doesn't support passkeys."); return; }
    setPasskeyBusy(true);
    setError("");
    try {
      const start = await passkeyLoginStart(email.trim());
      if (!start.allow_credential_ids?.length) {
        setError("No passkeys are set up for this email. Sign in with your password once, then add a passkey in Settings.");
        return;
      }
      const cred = await navigator.credentials.get({
        publicKey: {
          challenge: _b64urlDecode(start.challenge),
          allowCredentials: start.allow_credential_ids.map((id) => ({ id: _b64urlDecode(id), type: "public-key" })),
          userVerification: "preferred",
          timeout: 60_000,
        },
      });
      if (!cred) throw new Error("Cancelled");
      await passkeyLoginFinish({
        email: email.trim(),
        credential_id: _b64urlEncode(cred.rawId),
        challenge_token: start.challenge_token,
      });
      if (refreshAuth) await refreshAuth();
      navigate("/");
    } catch (err) {
      const msg = err?.name === "NotAllowedError" ? "Cancelled." : (err?.response?.data?.detail || err?.message || "Passkey sign-in failed.");
      setError(msg);
    } finally {
      setPasskeyBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && !ageConfirmed) {
      setError("Please confirm you are 13 or older to create an account.");
      return;
    }
    // Friend-invite flow — stash the referrer code in localStorage so the
    // existing attach_pending_referral path in /auth/register picks it up.
    if (mode === "register" && whoAmI === "friend" && friendRef.trim()) {
      try { localStorage.setItem("lectura_ref", friendRef.trim().toUpperCase()); } catch { /* noop */ }
    }
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name, whoAmI === "beta" ? code : "", ageConfirmed);
      // Route to the right follow-up destination per audience.
      if (groupCode) {
        navigate(`/group/${encodeURIComponent(groupCode)}`);
      } else if (mode === "register" && whoAmI === "teacher") {
        navigate("/teachers-wanted");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen lg:grid lg:grid-cols-2">
      {/* Marketing panel — visible on mobile above the form (D2.5) */}
      <div className="flex flex-col justify-between bg-primary text-primary-foreground p-6 sm:p-8 lg:p-12 relative z-10">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary-foreground/15">
            <BookOpen className="h-5 w-5" />
          </span>
          <span className="font-serif text-2xl font-bold">Lectura<span className="text-accent">.</span></span>
        </Link>
        <div className="mt-6 lg:mt-0">
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Your professor,<br />in your pocket.
          </h1>
          <p className="mt-3 sm:mt-4 text-sm sm:text-lg opacity-80 max-w-md">
            Turn any book chapter into a narrated lecture, slides, a printable study guide and a fun graded quiz.
          </p>
          <p className="mt-3 text-xs sm:text-sm font-semibold text-accent" data-testid="auth-no-cc">
            Free tier includes 3 lectures. No credit card required.
          </p>
          <div className="mt-4 sm:mt-6 flex flex-wrap gap-2" data-testid="auth-trust-badges">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
              ✓ FERPA-Ready
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
              ✓ No Student Data Training
            </span>
            <StandardsBadges variant="onDark" />
          </div>
          <Link
            to="/for-schools"
            data-testid="auth-for-schools-cta"
            className="mt-4 sm:mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
          >
            For schools &amp; districts →
          </Link>
        </div>
        <p className="hidden lg:block text-sm opacity-60 mt-6">Study smarter. Score higher.</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground">
              <BookOpen className="h-5 w-5" />
            </span>
            <span className="font-serif text-2xl font-bold">Lectura<span className="text-accent">.</span></span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl font-bold">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="mt-2 text-muted-foreground">
            {mode === "login" ? "Sign in to keep studying." : "Start learning in seconds — it's free."}
          </p>

          {referred && (
            <div data-testid="referred-banner" className="mt-6 rounded-xl border border-accent/40 bg-accent/10 px-4 py-3 text-sm">
              🎁 <span className="font-semibold">A friend invited you</span> — sign up and get <span className="font-semibold">7 free lectures</span> instead of 3.
            </div>
          )}

          <button
            data-testid="google-signin-btn"
            type="button"
            onClick={startGoogleSignIn}
            className="mt-8 w-full flex items-center justify-center gap-3 rounded-xl border-2 border-input bg-background py-3.5 font-semibold hover:bg-secondary/60 transition"
          >
            <GoogleGlyph />
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            or
            <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div data-testid="auth-whoami">
                <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Who are you?</label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {[
                    { k: "student", label: "Student / Parent" },
                    { k: "teacher", label: "Teacher / Educator" },
                    { k: "beta",    label: "I have a code" },
                    { k: "friend",  label: "A friend invited me" },
                  ].map((o) => (
                    <button
                      key={o.k}
                      type="button"
                      data-testid={`auth-whoami-${o.k}`}
                      onClick={() => setWhoAmI(o.k)}
                      className={`rounded-xl border-2 px-3 py-2 text-sm font-semibold transition ${
                        whoAmI === o.k ? "border-accent bg-accent/10 text-accent" : "border-border hover:bg-secondary/60"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {whoAmI === "teacher" && (
                  <p className="mt-1.5 text-[11px] text-muted-foreground">
                    We'll finish your sign-up then take you to the founding-teacher application (free Pro forever if you qualify).
                  </p>
                )}
              </div>
            )}
            {mode === "register" && (
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="auth-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full rounded-xl border border-input bg-background pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-ring/60"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                data-testid="auth-email"
                type={showEmail ? "email" : "password"}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.edu"
                autoComplete="username"
                className="w-full rounded-xl border border-input bg-background pl-11 pr-24 py-3 outline-none focus:ring-2 focus:ring-ring/60"
              />
              <button
                type="button"
                onClick={() => setShowEmail((v) => !v)}
                data-testid="auth-email-toggle"
                title={showEmail ? "Hide login ID" : "Show login ID"}
                aria-label={showEmail ? "Hide login ID" : "Show login ID"}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 rounded-lg bg-accent/10 hover:bg-accent/20 text-accent px-2.5 py-1.5 text-xs font-semibold transition"
              >
                {showEmail ? <><EyeOff className="h-4 w-4" /> Hide</> : <><Eye className="h-4 w-4" /> Show</>}
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                data-testid="auth-password"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handlePasswordKey}
                onKeyUp={handlePasswordKey}
                onBlur={() => setCapsOn(false)}
                placeholder="Password (min 6 characters)"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                className="w-full rounded-xl border border-input bg-background pl-11 pr-24 py-3 outline-none focus:ring-2 focus:ring-ring/60"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="auth-password-toggle"
                title={showPassword ? "Hide password" : "Show password"}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 rounded-lg bg-accent/10 hover:bg-accent/20 text-accent px-2.5 py-1.5 text-xs font-semibold transition"
              >
                {showPassword ? <><EyeOff className="h-4 w-4" /> Hide</> : <><Eye className="h-4 w-4" /> Show</>}
              </button>
            </div>

            {capsOn && (
              <div
                data-testid="caps-lock-warning"
                className="-mt-2 flex items-center gap-2 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-300 px-3 py-2 text-xs font-medium"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>Caps Lock is on.</span>
              </div>
            )}

            {mode === "register" && password && (
              <div className="-mt-2 space-y-1.5" data-testid="password-strength">
                <div className="flex gap-1" aria-hidden>
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className={`h-1 flex-1 rounded-full transition ${
                        i < strength.score ? strengthColors[strength.score] : "bg-border"
                      }`}
                    />
                  ))}
                </div>
                <p
                  data-testid="password-strength-label"
                  className={`text-xs font-medium ${
                    strength.score <= 1
                      ? "text-destructive"
                      : strength.score === 2
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-emerald-600 dark:text-emerald-400"
                  }`}
                >
                  {strength.label} — <span className="font-normal text-muted-foreground">{strength.hint}</span>
                </p>
              </div>
            )}

            {mode === "login" && (
              <div className="flex flex-wrap items-center justify-between -mt-2 gap-2">
                {_passkeysSupported && (
                  <button
                    type="button"
                    data-testid="passkey-signin-btn"
                    onClick={signInWithPasskey}
                    disabled={passkeyBusy || !email.trim()}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    {passkeyBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Fingerprint className="h-3.5 w-3.5" />}
                    Sign in with passkey
                  </button>
                )}
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="ml-auto inline-flex items-center gap-1 text-sm font-bold text-accent underline underline-offset-2 hover:text-accent/80"
                >
                  <KeyRound className="h-3.5 w-3.5" /> Forgot password?
                </Link>
              </div>
            )}

            {mode === "register" && whoAmI === "beta" && (
              <div className="relative">
                <Ticket className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="auth-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Your lifetime code — LECT-XXXX-XXXX"
                  className={`w-full rounded-xl border ${prefillCode ? "border-accent bg-accent/5" : "border-input bg-background"} pl-11 pr-4 py-3 font-mono uppercase outline-none focus:ring-2 focus:ring-ring/60`}
                />
                {prefillCode && (
                  <p className="mt-1 text-xs font-semibold text-accent" data-testid="auth-code-prefilled">
                    ✨ Code detected — lifetime Pro unlocks the moment you create your account.
                  </p>
                )}
                {!prefillCode && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Paste the code your inviter sent you. Each code works once.
                  </p>
                )}
              </div>
            )}
            {mode === "register" && whoAmI === "friend" && (
              <div className="relative">
                <Ticket className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="auth-friend-ref"
                  value={friendRef}
                  onChange={(e) => setFriendRef(e.target.value)}
                  placeholder="Your friend's invite code (optional)"
                  className={`w-full rounded-xl border ${prefillRef ? "border-accent bg-accent/5" : "border-input bg-background"} pl-11 pr-4 py-3 font-mono uppercase outline-none focus:ring-2 focus:ring-ring/60`}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  If your friend sent you a code, paste it so they get 30 days of free Pro when you sign up. Totally optional.
                </p>
              </div>
            )}

            {mode === "register" && (
              <p className="text-[11px] leading-relaxed text-muted-foreground -mt-1" data-testid="age-gate-notice">
                By creating an account you confirm you are <span className="font-semibold text-foreground">13 or older</span>, or your school has authorized your use of Lectura on behalf of a parent/guardian.
              </p>
            )}

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
            )}

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold disabled:opacity-50 hover:opacity-90 transition"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            {mode === "login" ? "New to Lectura?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="font-semibold text-accent hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          <div className="mt-8 pt-6 border-t border-border/60 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Just curious?</p>
            <Link
              to="/featured"
              data-testid="auth-featured-cta"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              Browse featured guides — no signup required →
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
